import { memo, useEffect, useState } from 'react'
import { useDebugInteractivity, useInteractiveElements } from './use-interactive'
import { isPointInBounds } from './registry'
import { twMerge } from 'tailwind-merge'

export const InteractiveDebugger = memo(() => {
  const interactiveElements = useInteractiveElements()
  const debugInteractivity = useDebugInteractivity()
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null)

  // Color each box by whether the cursor is actually inside it — the same
  // hit-test the main process uses to decide click-through — rather than the
  // global `isInteractive` flag. That flag is the renderer pointer-event
  // fast-path, which never fires for elements whose `<Interactive>` wraps
  // pointer-events-none content (e.g. toasts), so it paints them red even while
  // the bounds poll keeps them clickable. The overlay receives forwarded
  // mousemove even when click-through (`setIgnoreMouseEvents(true, forward)`).
  useEffect(() => {
    if (!debugInteractivity) return
    const onMove = (e: MouseEvent) => setCursor({ x: e.clientX, y: e.clientY })
    document.addEventListener('mousemove', onMove, true)
    return () => document.removeEventListener('mousemove', onMove, true)
  }, [debugInteractivity])

  if (!debugInteractivity) {
    return null
  }

  return Object.entries(interactiveElements).map(([id, bounds]) => {
    const active = cursor !== null && isPointInBounds(bounds, cursor.x, cursor.y)
    return (
      <div
        key={id}
        className={twMerge(
          'pointer-events-none absolute z-50 flex items-center justify-center',
          active ? 'bg-green/10' : 'bg-red/10',
        )}
        style={{ left: bounds.x, top: bounds.y, width: bounds.width, height: bounds.height }}
      >
        <span className="text-foreground text-xs font-medium">{id}</span>
      </div>
    )
  })
})
InteractiveDebugger.displayName = 'InteractiveDebugger'
